import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, json } from '../api/client'
import type { ApiKey, CreatedApiKey, Invite, InviteOffer, KeyScope, Member, Role, SentInvite, Workspace } from '../api/types'
import { meKey } from './useMe'

const workspaceKey = (id: string) => ['workspace', id] as const

export function useCreateWorkspace() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; slug?: string }) => api<Workspace>('/workspaces', { method: 'POST', body: json(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: meKey }),
  })
}

export function useUpdateWorkspace(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { name?: string; slug?: string }) => api<Workspace>(`/workspaces/${id}`, { method: 'PATCH', body: json(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: meKey }),
  })
}

export function useMembers(id: string) {
  return useQuery({ queryKey: [...workspaceKey(id), 'members'], queryFn: () => api<Member[]>(`/workspaces/${id}/members`) })
}

export function useApiKeys(id: string) {
  return useQuery({ queryKey: [...workspaceKey(id), 'api-keys'], queryFn: () => api<ApiKey[]>(`/workspaces/${id}/api-keys`) })
}

export function useCreateApiKey(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; scope: KeyScope }) =>
      api<CreatedApiKey>(`/workspaces/${id}/api-keys`, { method: 'POST', body: json(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...workspaceKey(id), 'api-keys'] }),
  })
}

export function useRevokeApiKey(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (keyId: string) => api<void>(`/workspaces/${id}/api-keys/${keyId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...workspaceKey(id), 'api-keys'] }),
  })
}

const membersKey = (id: string) => [...workspaceKey(id), 'members'] as const
const invitesKey = (id: string) => [...workspaceKey(id), 'invites'] as const

/** Invitations still waiting on an answer. Owners only. */
export function useInvites(id: string, enabled = true) {
  return useQuery({ queryKey: invitesKey(id), queryFn: () => api<Invite[]>(`/workspaces/${id}/invites`), enabled })
}

export function useInvite(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; role: Role }) => api<SentInvite>(`/workspaces/${id}/invites`, { method: 'POST', body: json(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invitesKey(id) }),
  })
}

export function useCancelInvite(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (inviteId: string) => api<void>(`/workspaces/${id}/invites/${inviteId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invitesKey(id) }),
  })
}

export function useSetMemberRole(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { userId: string; role: Role }) =>
      api<void>(`/workspaces/${id}/members/${input.userId}`, { method: 'PATCH', body: json({ role: input.role }) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: membersKey(id) })
      void queryClient.invalidateQueries({ queryKey: meKey })
    },
  })
}

/** Take someone out, or leave when it is your own id. */
export function useRemoveMember(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api<void>(`/workspaces/${id}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: membersKey(id) })
      void queryClient.invalidateQueries({ queryKey: meKey })
    },
  })
}

/** What the person who was invited sees. No sign-in needed to read it. */
export function useInviteOffer(token: string) {
  return useQuery({ queryKey: ['invite', token], queryFn: () => api<InviteOffer>(`/invites/${token}`), retry: false })
}

export function useAcceptInvite(token: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api<{ workspace_id: string }>(`/invites/${token}/accept`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: meKey })
      void queryClient.invalidateQueries({ queryKey: ['invite', token] })
    },
  })
}
